/************************************************************************************************
 * Copyright TRUSST AI PTY LTD. All Rights Reserved.                                            *
 *                                                                                              *
 * Licensed under the TRUSST SOFTWARE LICENSE (the "License"). You may not use this file except *
 * in compliance with the "LICENSE" file accompanying this file. This file is distributed       *
 * on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, express or implied.       *
 *                                                                                              *
 * See the "License" file for the specific language governing permissions and limitations       *
 * under the License and limitations under the License.                                         *
 ***********************************************************************************************/

import {
  Box,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  Stack,
  Typography,
  Tooltip,
} from '@mui/material';
import React, {useEffect, useState} from 'react';
import {LoadingSpinner} from '../../../components/LoadingSpinner';
import {useGetAgentPhoneNumbers} from '../hooks/useGetAgentPhoneNumbers';

type AgentPhoneNumbersProps = {
  agentPhoneNumbers: string[];
  agentId: string | undefined;
  onPhoneNumbersChange: (phoneNumbers: string[]) => void;
};

export const AgentPhoneNumbers: React.FC<AgentPhoneNumbersProps> = ({
  agentPhoneNumbers = [],
  agentId,
  onPhoneNumbersChange,
}) => {
  const {phoneNumbers, isLoading, fetch: fetchPhoneNumbers} = useGetAgentPhoneNumbers();
  const [selectedPhoneNumbers, setSelectedPhoneNumbers] = useState<string[]>(agentPhoneNumbers);

  useEffect(() => {
    void fetchPhoneNumbers();
  }, [fetchPhoneNumbers]);

  useEffect(() => {
    setSelectedPhoneNumbers(agentPhoneNumbers);
  }, [agentPhoneNumbers]);

  const handlePhoneNumberChange = (event: SelectChangeEvent<string[]>) => {
    // The event.target.value might be a string when deselecting with commas
    const value =
      typeof event.target.value === 'string' ? event.target.value.split(',').map((v) => v.trim()) : event.target.value;

    setSelectedPhoneNumbers(value);
    onPhoneNumbersChange(value);
  };

  return (
    <Card>
      <CardContent>
        <Stack spacing={2}>
          <Typography variant={'body1'}>{'Agent Phone Numbers'}</Typography>
          {isLoading ? (
            <Box display={'flex'} justifyContent={'center'}>
              <LoadingSpinner size={'md'} />
            </Box>
          ) : (
            <>
              <FormControl fullWidth>
                <InputLabel id={'phone-numbers-select-label'}>{'Phone Numbers'}</InputLabel>
                <Select
                  labelId={'phone-numbers-select-label'}
                  id={'phone-numbers-select'}
                  multiple
                  value={selectedPhoneNumbers}
                  onChange={handlePhoneNumberChange}
                  input={<OutlinedInput label={'Phone Numbers'} />}
                  renderValue={(selected) => (Array.isArray(selected) ? selected.join(', ') : selected)}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 300,
                      },
                    },
                  }}
                >
                  {phoneNumbers.map((item) => {
                    const isAssignedToOtherAgent = item.agentId && item.agentId !== agentId;
                    const phoneNumber = item.phoneNumber;
                    const isSelected = selectedPhoneNumbers.includes(phoneNumber);

                    if (isAssignedToOtherAgent) {
                      return (
                        <Tooltip
                          key={phoneNumber}
                          title={`This number is assigned to ${item.agentName || 'another agent'}. Unassign it first.`}
                          placement={'right'}
                          arrow
                        >
                          <MenuItem value={phoneNumber} disabled={true} sx={{opacity: 0.6}}>
                            {phoneNumber}
                            <Typography variant={'caption'} color={'error.main'} sx={{ml: 1}}>
                              {item.agentName ? `(assigned to ${item.agentName})` : '(assigned)'}
                            </Typography>
                          </MenuItem>
                        </Tooltip>
                      );
                    }

                    return (
                      <MenuItem key={phoneNumber} value={phoneNumber} sx={{fontWeight: isSelected ? 'bold' : 'normal'}}>
                        {phoneNumber}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
              <Typography variant={'caption'} sx={{color: 'text.secondary'}}>
                Incoming calls to these phone numbers will be routed directly to this agent. Unassigned numbers default
                to the Default agent. To reassign a number currently linked to another agent, you must first remove it
                from that agent.
              </Typography>
            </>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};
